<div class="comparables" [class.embedded]="embedded">
  <div *ngIf="!embedded" class="mb-3">
    <h2>Comparables</h2>
    <div class="text-muted small">
      Evidence collection only — no valuation, range, weighting, recency, index,
      confidence or blending. Live read-only against the prod replica.
    </div>
  </div>

  <form class="row g-2 align-items-end" (ngSubmit)="run()">
    <div class="col-auto">
      <label class="form-label small mb-1" for="comps-plate">Plate</label>
      <input id="comps-plate" name="plate" class="form-control text-uppercase" style="width: 130px"
             [(ngModel)]="plate" placeholder="J23MES" required />
    </div>
    <div class="col-auto">
      <label class="form-label small mb-1" for="comps-term">Term (optional)</label>
      <input id="comps-term" name="term" class="form-control text-uppercase" style="width: 130px"
             [(ngModel)]="term" placeholder="JAMES" />
    </div>
    <div class="col-auto">
      <label class="form-label small mb-1" for="comps-limit">Row cap</label>
      <input id="comps-limit" name="limit" type="number" min="1" max="1000" class="form-control" style="width: 90px"
             [(ngModel)]="limit" />
    </div>
    <div class="col-auto">
      <label class="form-label small mb-1" for="comps-min-score">Min term score</label>
      <input id="comps-min-score" name="minScore" type="number" min="0" max="100" class="form-control" style="width: 90px"
             [(ngModel)]="minScore" />
    </div>
    <div class="col-auto">
      <label class="form-label small mb-1" for="comps-band">Peer band ±</label>
      <input id="comps-band" name="band" type="number" min="0" max="50" class="form-control" style="width: 80px"
             [(ngModel)]="band" />
    </div>
    <div class="col-auto">
      <button type="submit" class="btn btn-primary" [disabled]="loading || !plate">
        {{ loading ? 'Collecting…' : 'Collect' }}
      </button>
    </div>
  </form>

  <div *ngIf="loading" class="text-muted mt-3">
    Running bounded read-only queries against the prod replica…
  </div>
  <div *ngIf="error" class="alert alert-warning mt-3">{{ error }}</div>

  <ng-container *ngIf="result">
    <!-- classification summary -->
    <div class="d-flex gap-2 flex-wrap mt-3">
      <span class="badge bg-secondary">{{ result.plate }}</span>
      <span class="badge bg-light text-dark">style: {{ result.classification.style }}</span>
      <span class="badge bg-light text-dark">
        {{ result.classification.driver === 'meaning' ? 'meaning-driven' : 'structure-driven' }}
      </span>
      <span *ngIf="result.classification.dateless_subgroup" class="badge bg-light text-dark">
        subgroup: {{ result.classification.dateless_subgroup }}
      </span>
      <span *ngIf="result.classification.subject_term_score !== null" class="badge bg-info text-dark">
        subject term-score: {{ score(result.classification.subject_term_score) }}
      </span>
      <span class="badge bg-light text-dark">routes: {{ result.routes_executed.join(', ') || 'none' }}</span>
    </div>
    <div *ngFor="let n of result.notes" class="text-muted small mt-1">• {{ n }}</div>

    <!-- Genuine sold -->
    <h5 class="mt-4">
      Genuine Sold Comparables
      <span class="badge bg-success">{{ sectionCount(result.sections.genuine_sold) }}</span>
    </h5>
    <div class="text-muted small mb-1">
      Completed transactions, last 5 years: verified auctions (DVLA / CC / Brightwells / Bonhams)
      and DVLA fixed-price sales. Same-letter plates first, then newest.
    </div>
    <div class="table-responsive">
      <table class="table table-sm table-striped align-middle">
        <thead>
          <tr>
            <th>Registration</th><th>Style</th><th class="text-end">Sale price</th>
            <th>Sale date</th><th>Source</th><th>Route</th>
            <th *ngIf="hasTerm" class="text-end">Term score</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of result.sections.genuine_sold.rows">
            <td class="fw-bold">{{ r.registration }}</td>
            <td>{{ r.style || '—' }}</td>
            <td class="text-end">{{ gbp(r.price_pence) }}</td>
            <td>{{ r.date || '—' }}</td>
            <td>{{ r.source || '—' }}</td>
            <td class="small text-muted">{{ r.route || '—' }}</td>
            <td *ngIf="hasTerm" class="text-end">{{ score(r.term_score) }}</td>
          </tr>
          <tr *ngIf="!result.sections.genuine_sold.rows.length">
            <td colspan="7" class="text-muted">No genuine sold comparables found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Current market -->
    <h5 class="mt-4">
      Current Market Comparables
      <span class="badge bg-primary">{{ sectionCount(result.sections.current_market) }}</span>
    </h5>
    <div class="text-muted small mb-1">
      Asking prices only (PlateX listings, dealer stock, available DVLA registrations).
    </div>
    <div class="table-responsive">
      <table class="table table-sm table-striped align-middle">
        <thead>
          <tr>
            <th>Registration</th><th>Style</th><th class="text-end">Asking price</th>
            <th>Dealer / Source</th><th>Route</th>
            <th *ngIf="hasTerm" class="text-end">Term score</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of result.sections.current_market.rows">
            <td class="fw-bold">{{ r.registration }}</td>
            <td>{{ r.style || '—' }}</td>
            <td class="text-end">{{ gbp(r.price_pence) }}</td>
            <td>{{ src(r) }}</td>
            <td class="small text-muted">{{ r.route || '—' }}</td>
            <td *ngIf="hasTerm" class="text-end">{{ score(r.term_score) }}</td>
          </tr>
          <tr *ngIf="!result.sections.current_market.rows.length">
            <td colspan="6" class="text-muted">No current market comparables found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Previous market -->
    <h5 class="mt-4">
      Previous Market Comparables
      <span class="badge bg-warning text-dark">{{ sectionCount(result.sections.previous_market) }}</span>
    </h5>
    <div class="text-muted small mb-1">
      Prior market activity (dealer-marked &amp; price-change), last 5 years — not current
      asking, not a verified sale.
    </div>
    <div class="table-responsive">
      <table class="table table-sm table-striped align-middle">
        <thead>
          <tr>
            <th>Registration</th><th>Style</th><th class="text-end">Recorded price</th>
            <th>Date</th><th>Source</th><th>Route</th>
            <th *ngIf="hasTerm" class="text-end">Term score</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of result.sections.previous_market.rows">
            <td class="fw-bold">{{ r.registration }}</td>
            <td>{{ r.style || '—' }}</td>
            <td class="text-end">{{ gbp(r.price_pence) }}</td>
            <td>{{ r.date || '—' }}</td>
            <td>{{ r.source || '—' }}</td>
            <td class="small text-muted">{{ r.route || '—' }}</td>
            <td *ngIf="hasTerm" class="text-end">{{ score(r.term_score) }}</td>
          </tr>
          <tr *ngIf="!result.sections.previous_market.rows.length">
            <td colspan="7" class="text-muted">No previous market comparables found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Term peers -->
    <ng-container *ngIf="hasTerm && result.sections.term_peers.total > 0">
      <h5 class="mt-4">
        Term-score peers (any shape)
        <span class="badge bg-info text-dark">±{{ result.score_band }}</span>
        <span class="badge bg-secondary">{{ sectionCount(result.sections.term_peers) }}</span>
      </h5>
      <div class="text-muted small mb-1">
        Plates of any shape that represent <b>{{ result.term }}</b> about as well as
        {{ result.plate }} does, and carry the term's first letter ahead of the rest so
        they read as the word.
      </div>
      <div class="table-responsive">
        <table class="table table-sm table-striped align-middle">
          <thead>
            <tr>
              <th>Registration</th><th>Style</th><th>Lane</th>
              <th class="text-end">Term score</th><th class="text-end">Price</th>
              <th>Source</th><th>Route</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let r of result.sections.term_peers.rows">
              <td class="fw-bold">{{ r.registration }}</td>
              <td>{{ r.style || '—' }}</td>
              <td>{{ lane(r) }}</td>
              <td class="text-end">{{ score(r.term_score) }}</td>
              <td class="text-end">{{ gbp(r.price_pence) }}</td>
              <td>{{ src(r) }}</td>
              <td class="small text-muted">{{ r.route || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <!-- audit (collapsed) -->
    <details class="mt-4">
      <summary>
        Route summary &amp; query audit
        <span class="badge bg-light text-dark">read-only · bounded</span>
        <span *ngIf="queryErrors" class="badge bg-warning text-dark">{{ queryErrors }} query error(s)</span>
      </summary>
      <div class="table-responsive mt-2">
        <table class="table table-sm">
          <thead><tr><th>Route</th><th class="text-end">Genuine sold</th><th class="text-end">Previous market</th><th class="text-end">Current market</th></tr></thead>
          <tbody>
            <tr *ngFor="let r of result.route_summary">
              <td class="small">{{ r.route }}</td>
              <td class="text-end">{{ r.genuine_sold }}</td>
              <td class="text-end">{{ r.previous_market }}</td>
              <td class="text-end">{{ r.current_market }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-responsive mt-2">
        <table class="table table-sm">
          <thead><tr><th>Query</th><th>DB</th><th class="text-end">Rows</th><th class="text-end">Limit</th><th>Capped</th><th>Error</th></tr></thead>
          <tbody>
            <tr *ngFor="let q of result.query_log">
              <td class="small">{{ q.label }}</td>
              <td class="small">{{ q.db }}</td>
              <td class="text-end">{{ q.rows }}</td>
              <td class="text-end">{{ q.limit }}</td>
              <td>{{ q.capped ? 'yes' : 'no' }}</td>
              <td class="small text-danger">{{ q.error || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </details>
  </ng-container>
</div>
